import { auth } from "../config/firebase";
import { SubscriptionStatus } from "@beyondrealityapp/core/shared/constants";
import SubscriptionGuardView from "./SubscriptionGuardView";
import { useEffect, useState } from "react";

const ACTIVE_STATUSES: string[] = [
  SubscriptionStatus.ACTIVE,
  SubscriptionStatus.TRIALING,
  SubscriptionStatus.EARLY_ADOPTER,
  SubscriptionStatus.CANCEL_PENDING,
];

/** Maximum number of retries when claims are not yet available. */
const MAX_RETRIES = 5;
/** Delay between retries in milliseconds. */
const RETRY_DELAY_MS = 2000;

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

const SubscriptionGuard: React.FC = () => {
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const checkSubscription = async () => {
      const user = auth.currentUser;
      if (!user) {
        setAllowed(false);
        return;
      }

      // First check with cached token
      const idTokenResult = await user.getIdTokenResult();
      const status = idTokenResult.claims.subscriptionStatus as
        | string
        | undefined;

      // Admin users bypass subscription checks
      if (idTokenResult.claims.admin) {
        setAllowed(true);
        return;
      }

      if (status && ACTIVE_STATUSES.includes(status)) {
        setAllowed(true);
        return;
      }

      // If status is already a known non-active value, block immediately
      if (
        status &&
        Object.values(SubscriptionStatus).includes(status as SubscriptionStatus)
      ) {
        setAllowed(false);
        return;
      }

      // Status is undefined — custom claims may not have propagated yet
      // (e.g. right after sign-up). Retry with forced token refresh.
      setIsRetrying(true);
      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        console.log(
          `SubscriptionGuard: retrying to fetch claims (attempt ${attempt + 1}/${MAX_RETRIES})`,
        );
        if (cancelled) return;
        await wait(RETRY_DELAY_MS);
        if (cancelled) return;

        const refreshed = await user.getIdTokenResult(true);
        const refreshedStatus = refreshed.claims.subscriptionStatus as
          | string
          | undefined;

        if (refreshedStatus && ACTIVE_STATUSES.includes(refreshedStatus)) {
          setAllowed(true);
          return;
        }

        // If it resolved to a known non-active status, stop retrying
        if (
          refreshedStatus &&
          Object.values(SubscriptionStatus).includes(
            refreshedStatus as SubscriptionStatus,
          )
        ) {
          setAllowed(false);
          return;
        }
      }

      // Exhausted retries — claims never arrived
      setAllowed(false);
    };

    checkSubscription();
    return () => {
      cancelled = true;
    };
  }, []);

  return <SubscriptionGuardView allowed={allowed} isRetrying={isRetrying} />;
};

export default SubscriptionGuard;
